import React, { useState, useEffect } from "react";
import { useAuth } from "../AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "./Home.css";

export default function Login() {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get("mode") === "signup" ? false : true;
  const redirectTo = searchParams.get("redirect") || "/";

  const [isLogin, setIsLogin] = useState(initialMode);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { login, signup, googleLogin } = useAuth();
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  const toggleForm = () => {
    setIsLogin((v) => !v);
    setError("");
    setSuccess("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      if (isLogin) {
        // ─── LOGIN ───────────────────────────────────────
        const result = await login(email, password);
        setSuccess("Login successful! Redirecting...");

        // If owner has not completed onboarding, go to /onboarding
        const step = result?.onboardingStep ?? 7;
        setTimeout(() => {
          if (step < 7 && redirectTo === "/") {
            navigate("/onboarding");
          } else {
            navigate(redirectTo);
          }
        }, 1200);
      } else {
        // ─── SIGN UP ─────────────────────────────────────
        if (!name.trim()) { setError("Full name is required."); setLoading(false); return; }
        if (!email.trim()) { setError("Email is required."); setLoading(false); return; }
        if (!phone.trim() || !/^\d{10}$/.test(phone)) { setError("Enter a valid 10-digit phone number."); setLoading(false); return; }
        if (password.length < 8) { setError("Password must be at least 8 characters."); setLoading(false); return; }
        if (password !== confirmPassword) { setError("Passwords do not match."); setLoading(false); return; }

        await signup(name.trim(), email.trim(), phone.trim(), password);
        setSuccess("Account created! Please verify your email to continue.");
        setTimeout(() => navigate("/onboarding"), 1400);
      }
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  // Initialize Google Identity Services
  useEffect(() => {
    if (!clientId) return;
    const id = "gsi-script-login";
    if (document.getElementById(id)) return;

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.id = id;
    document.body.appendChild(script);

    script.onload = () => {
      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response) => {
            try {
              const idToken = response?.credential;
              if (!idToken) throw new Error("Missing credential from Google");
              const result = await googleLogin(idToken);
              setSuccess("Google Sign-In successful! Redirecting...");
              const step = result?.onboardingStep ?? 7;
              setTimeout(() => {
                if (step < 7 && redirectTo === "/") navigate("/onboarding");
                else navigate(redirectTo);
              }, 1200);
            } catch (err) {
              setError(err?.message || String(err));
            }
          },
        });
        const container = document.getElementById("g_id_signin_login");
        if (container) {
          window.google.accounts.id.renderButton(container, {
            theme: "outline",
            size: "large",
            width: "100%",
          });
        }
      } catch (err) {}
    };
    script.onerror = () => setError("Failed to load Google Sign-In.");
    return () => {
      try { document.getElementById(id)?.remove(); } catch {}
    };
  }, [clientId, googleLogin, navigate, redirectTo]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-900 flex items-center justify-center px-4 py-12">
      {/* Decorative orbs */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/20 rounded-full blur-[100px] pointer-events-none" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-indigo-500/15 rounded-full blur-[120px] pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="relative w-full max-w-md"
      >
        {/* Card */}
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-10">
          {/* Logo / Brand */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-4">
              <img
                src="/images/trivanalogo.jpg"
                alt="Trivana"
                className="h-10 w-10 rounded-xl object-contain shadow"
              />
              <span
                className="text-xl font-bold text-white"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                Trivana Hospitality
              </span>
            </div>
            <h1
              className="text-2xl sm:text-3xl font-extrabold text-white mb-1"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              {isLogin ? "Welcome back" : "Start your journey"}
            </h1>
            <p className="text-white/60 text-sm">
              {isLogin
                ? "Sign in to your owner account"
                : "Create your Trivana owner account"}
            </p>
          </div>

          {/* Alerts */}
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-5 p-3 bg-green-500/20 border border-green-400/40 rounded-xl text-green-200 text-sm text-center"
            >
              {success}
            </motion.div>
          )}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-5 p-3 bg-red-500/20 border border-red-400/40 rounded-xl text-red-200 text-sm text-center"
            >
              {error}
              {error.toLowerCase().includes("email not verified") && (
                <div className="mt-2">
                  <a href="/resend-verification" className="text-blue-300 underline text-xs">
                    Resend verification email
                  </a>
                </div>
              )}
            </motion.div>
          )}

          {/* Form */}
          <form className="space-y-4" onSubmit={handleSubmit}>
            {/* Name — signup only */}
            {!isLogin && (
              <div>
                <label className="block text-white/70 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your full name"
                  className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-blue-400/60 text-sm transition"
                />
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-white/70 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                Email Address *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-blue-400/60 text-sm transition"
              />
            </div>

            {/* Phone — signup only */}
            {!isLogin && (
              <div>
                <label className="block text-white/70 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                  Phone Number *
                </label>
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white/10 border border-white/20 focus-within:ring-2 focus-within:ring-blue-400/60 transition">
                  <span className="text-white/50 text-sm">+91</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="9876543210"
                    className="flex-1 bg-transparent text-white placeholder:text-white/30 focus:outline-none text-sm"
                  />
                </div>
              </div>
            )}

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-white/70 text-xs font-semibold uppercase tracking-wide">
                  Password *
                </label>
                {isLogin && (
                  <a href="/forgot-password" className="text-blue-300 text-xs hover:text-blue-200 transition">
                    Forgot password?
                  </a>
                )}
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={isLogin ? "••••••••" : "Min 8 characters"}
                  required
                  className="w-full px-4 py-3 pr-11 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-blue-400/60 text-sm transition"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition"
                >
                  {showPassword ? <FaEyeSlash size={15} /> : <FaEye size={15} />}
                </button>
              </div>
            </div>

            {/* Confirm password — signup only */}
            {!isLogin && (
              <div>
                <label className="block text-white/70 text-xs font-semibold mb-1.5 uppercase tracking-wide">
                  Confirm Password *
                </label>
                <div className="relative">
                  <input
                    type={showConfirm ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    className="w-full px-4 py-3 pr-11 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-blue-400/60 text-sm transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition"
                  >
                    {showConfirm ? <FaEyeSlash size={15} /> : <FaEye size={15} />}
                  </button>
                </div>
              </div>
            )}

            {/* Submit */}
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: loading ? 1 : 1.02 }}
              whileTap={{ scale: loading ? 1 : 0.98 }}
              className="w-full py-3.5 rounded-xl font-bold text-white bg-gradient-to-r from-blue-500 to-indigo-600 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 disabled:opacity-60 disabled:cursor-not-allowed transition-all text-sm mt-2"
              style={{ fontFamily: "Montserrat, sans-serif" }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {isLogin ? "Signing in..." : "Creating account..."}
                </span>
              ) : isLogin ? "Sign In" : "Create Account"}
            </motion.button>
          </form>

          {/* Divider */}
          <div className="flex items-center my-5">
            <hr className="flex-1 border-white/15" />
            <span className="mx-3 text-white/40 text-xs font-medium">OR</span>
            <hr className="flex-1 border-white/15" />
          </div>

          {/* Google Sign-In */}
          <div id="g_id_signin_login" className="flex justify-center" />
          {!clientId && (
            <p className="text-center text-white/40 text-xs mt-2">
              Google Sign-In not configured.
            </p>
          )}

          {/* Toggle */}
          <p className="text-center text-white/50 text-sm mt-6">
            {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
            <button
              onClick={toggleForm}
              className="text-blue-300 font-semibold hover:text-blue-200 hover:underline transition"
            >
              {isLogin ? "Sign Up" : "Sign In"}
            </button>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
